import { Theme, ThemeOptions } from '@mui/material';

declare module '@mui/material/styles' {
    interface Palette {
        palmas: {
            subtle: string;
            textLight: string;
            textDark: string;
        }
    }

    interface PaletteOptions {
        palmas?: {
            subtle: string;
            textLight: string;
            textDark: string;
        }
    }
}

declare module '@mui/material/styles' {
    interface BreakpointOverrides {
        xs: true;
        sm: true;
        md: true;
        lg: true;
        xl: true;
        mobile: true;
        tablet: true;
        laptop: true;
        desktop: true;
    }
}

declare module '@mui/material/Button' {
    interface ButtonPropsVariantOverrides {
        navTab: true;
    }
}

const themeConfig: ThemeOptions = {
    palette: {
        mode: "light",
        background: {
            paper: "#ffffff",
            default: "#f8fafc",
        },
        common: {
            black: "#111827",
            white: "#ffffff",
        },
        primary: {
            main: "#3b82f6",
            light: "#93c5fd",
            dark: "#2563eb",
            contrastText: "#ffffff",
        },
        secondary: {
            main: "#8B5CF6",
            light: "#A78BFA",
            dark: "#7C3AED",
            contrastText: "#ffffff",
        },
        info: {
            main: "#3B82F6",
            light: "#60A5FA",
            dark: "#2563EB",
            contrastText: "#ffffff",
        },
        warning: {
            main: "#F59E0B",
            light: "#FBBF24",
            dark: "#D97706",
            contrastText: "#ffffff",
        },
        success: {
            main: "#059669",
            light: "#10B981",
            dark: "#047857",
            contrastText: "#ffffff",
        },
        error: {
            main: "#EF4444",
            light: "#F87171",
            dark: "#B91C1C",
            contrastText: "#ffffff",
        },
        text: {
            primary: "#111827",
            secondary: "#4b5563",
        },
        palmas: {
            subtle: 'rgba(9, 30, 66, 0.08)',
            textLight: '#42526E',
            textDark: '#172B4D'
        },
        action: {
            hover: 'rgba(9, 30, 66, 0.08)'
        }
    },
    components: {
        MuiButton: {
            styleOverrides: {
                root: {
                    textTransform: "none" as const,
                    borderRadius: "8px",
                    fontWeight: 500,
                    transition: "all 0.2s ease-in-out",
                    '&.status-pending': {
                        backgroundColor: 'rgba(245, 158, 11, 0.1)',
                        color: '#f59e0b',
                        '&:hover': {
                            backgroundColor: 'rgba(245, 158, 11, 0.2)',
                        }
                    },
                    '&.status-completed': {
                        backgroundColor: 'rgba(16, 185, 129, 0.1)',
                        color: '#10b981',
                        '&:hover': {
                            backgroundColor: 'rgba(16, 185, 129, 0.2)',
                        }
                    }
                },
                outlined: {
                    "&.Mui-disabled": {
                        borderColor: 'rgba(0, 0, 0, 0.12)',
                        color: 'rgba(0, 0, 0, 0.26)',
                    },
                },
                contained: {
                    "&.Mui-disabled": {
                        backgroundColor: 'rgba(0, 0, 0, 0.12)',
                        color: 'rgba(0, 0, 0, 0.26)',
                    },
                },
            },
            variants: [{
                props: { variant: 'navTab' as const },
                style: ({ theme }) => ({
                    color: theme.palette.palmas.textLight,
                    minWidth: 60,
                    padding: '0 12px',
                    '&.Mui-active': {
                        color: theme.palette.palmas.textDark,
                        backgroundColor: theme.palette.action.hover
                    },
                    '&:hover': {
                        backgroundColor: theme.palette.action.hover,
                        color: theme.palette.palmas.textDark
                    }
                })
            }]
        },
        MuiIconButton: {
            styleOverrides: {
                root: {
                    color: '#42526E',
                    '&:hover': {
                        backgroundColor: 'rgba(9, 30, 66, 0.08)',
                        color: '#172B4D'
                    }
                },
            },
        },
        MuiPaper: {
            styleOverrides: {
                root: {
                    backgroundImage: "none",
                    boxShadow: "0 1px 3px rgba(0,0,0,0.05), 0 1px 2px rgba(0,0,0,0.1)",
                    '&.MuiDrawer-paper': {
                        backgroundColor: 'background.paper',
                    },
                    '&.priority-area': {
                        transition: 'all 0.2s ease',
                        '&:hover': {
                            backgroundColor: 'rgba(255, 255, 255, 0.9)',
                        },
                    },
                    '&.todo-item': {
                        backgroundColor: '#ffffff',
                        borderRadius: '8px',
                        transition: 'all 0.2s ease',
                        border: '1px solid',
                        borderColor: 'rgba(0, 0, 0, 0.08)',
                        '&:hover': {
                            backgroundColor: '#ffffff',
                            borderColor: 'rgba(0, 0, 0, 0.12)',
                            transform: 'translateY(-1px)',
                            boxShadow: '0 2px 4px rgba(0,0,0,0.05), 0 2px 10px rgba(0,0,0,0.05)',
                        },
                    },
                },
            },
        },
        MuiDrawer: {
            styleOverrides: {
                paper: {
                    backgroundColor: 'background.paper',
                },
            },
        },
        MuiSelect: {
            styleOverrides: {
                select: {
                    backgroundColor: 'background.paper',
                },
            },
        },
        MuiMenu: {
            styleOverrides: {
                paper: {
                    backgroundColor: 'background.paper',
                },
            },
        },
        MuiPopover: {
            styleOverrides: {
                paper: {
                    backgroundColor: 'background.paper',
                },
            },
        },
        MuiInputBase: {
            styleOverrides: {
                root: {
                    backgroundColor: 'background.paper',
                    '&.Mui-focused': {
                        backgroundColor: 'background.paper',
                    },
                },
            },
        },
        MuiTypography: {
            styleOverrides: {
                root: {
                    color: 'text.primary',
                    transition: "color 0.2s ease",
                },
            },
            defaultProps: {
                color: 'text.primary'
            }
        },
        MuiInputLabel: {
            styleOverrides: {
                root: {
                    fontSize: "0.875rem",
                },
            },
        },
        MuiTextField: {
            styleOverrides: {
                root: {
                    '& .MuiOutlinedInput-root': {
                        transition: 'all 0.2s ease-in-out',
                        backgroundColor: "#fff",
                        '&:hover': {
                            borderColor: '#3b82f6',
                        },
                        '&.Mui-focused': {
                            boxShadow: '0 0 0 2px rgba(59, 130, 246, 0.2)',
                        },
                    },
                },
            },
        },
        MuiMenuItem: {
            styleOverrides: {
                root: {
                    '& .MuiListItemIcon-root': {
                        color: 'inherit',
                        minWidth: 36
                    }
                }
            }
        },
        MuiListItemIcon: {
            styleOverrides: {
                root: {
                    color: 'inherit'
                }
            }
        }
    },
    breakpoints: {
        values: {
            xs: 0,
            sm: 600,
            md: 900,
            lg: 1200,
            xl: 1536,
            mobile: 0,
            tablet: 640,
            laptop: 1024,
            desktop: 1200,
        },
    },
    typography: {
        h1: {
            fontSize: "80px",
            fontWeight: 400,
            lineHeight: "51px",
            letterSpacing: "0.15px",
        },
        h2: {
            fontSize: "64px",
            fontWeight: 400,
            lineHeight: "48px",
            letterSpacing: "0.15px",
        },
        h3: {
            fontSize: "48px",
            fontWeight: 400,
            lineHeight: "17.16px",
            letterSpacing: "0.15px",
        },
        h4: {
            fontSize: "32px",
            fontWeight: 500,
            lineHeight: "40px",
            letterSpacing: "0.17px",
        },
        h5: {
            fontSize: "24px",
            fontWeight: 500,
            lineHeight: "36px",
            letterSpacing: "0.15px",
        },
        h6: {
            fontSize: "20px",
            fontWeight: 500,
            lineHeight: "32px",
            letterSpacing: "0.15px",
        },
        subtitle1: {
            fontSize: "16px",
            fontWeight: 500,
            lineHeight: "24px",
            letterSpacing: "0.15px",
        },
        subtitle2: {
            fontSize: "14px",
            fontWeight: 500,
            lineHeight: "24px",
            letterSpacing: "0.15px",
        },
        body1: {
            fontSize: "14px",
            fontWeight: 500,
            lineHeight: "24px",
            letterSpacing: "0.17px",
        },
        body2: {
            fontSize: "12px",
            fontWeight: 400,
            lineHeight: "21px",
            letterSpacing: "0.15px",
        },
        caption: {
            fontSize: "12px",
            fontWeight: 400,
            lineHeight: "17.16px",
            letterSpacing: "0.15px",
        },

        fontFamily: "Poppins",
    },
};

export default themeConfig;
